import _ from 'underscore';
import type { Environment } from '../../environment';
import type {
  EntityKey, EntityObject, SideEffectCacheStoreHelpers,
} from '../../public-types';
import { OVERREACT_ID_FIELD_NAME, createOverreactId } from '../../store/consts';

export function getSideEffectCacheStoreHelpers(
  environment: Environment,
): SideEffectCacheStoreHelpers {
  const { store, schema: { schemaToModelMapping } } = environment;

  const tryMergeItemsToCacheStore = <TEntity extends EntityObject>({
    items,
    schemaName,
    itemKeySelector,
    parentSchemaName,
    parentId,
  }: {
    items: TEntity[];
    schemaName: string;
    itemKeySelector: (_item: TEntity) => EntityKey;
    parentSchemaName: string;
    parentId: EntityKey;
  }) => {
    const recordGroup = store.getRecordGroup(schemaToModelMapping[schemaName]);
    const keysInCache = recordGroup.records.map(record => itemKeySelector(record.data as TEntity));
    const itemsToAdd = _.filter(items, item => !_.include(keysInCache, itemKeySelector(item)));
    const itemsToAddWithOverreactId = itemsToAdd.map(item => ({
      rawData: item,
      [OVERREACT_ID_FIELD_NAME]: createOverreactId(
        itemKeySelector(item),
        parentId,
        parentSchemaName,
      ),
    }));
    recordGroup.addOrUpdateRecords(itemsToAddWithOverreactId);
    const ids = itemsToAddWithOverreactId.map(item => item[OVERREACT_ID_FIELD_NAME]);
    recordGroup.notify('entitiesCreated', ids);

    const itemsToMerge = _.filter(items, item => _.include(keysInCache, itemKeySelector(item)));
    const itemsMerged = itemsToMerge.map(itemToMerge => {
      const records = recordGroup.getRecordsByEntityKeys(
        entity => itemKeySelector(entity as TEntity),
        [itemKeySelector(itemToMerge)],
      );
      return {
        rawData: { ...records[0].data, ...itemToMerge },
        [OVERREACT_ID_FIELD_NAME]: records[0].id,
      };
    });
    recordGroup.addOrUpdateRecords(itemsMerged);
  };

  const tryDeleteItemsInCacheStore = <TEntity extends EntityObject>({
    keysToDelete,
    schemaName,
    itemKeySelector,
  }: {
    keysToDelete: EntityKey[];
    schemaName: string;
    itemKeySelector: (_item: TEntity) => EntityKey;
  }) => {
    const recordGroup = store.getRecordGroup(schemaToModelMapping[schemaName]);
    const recordsToRemove = recordGroup.getRecordsByEntityKeys(
      entity => itemKeySelector(entity as TEntity),
      keysToDelete,
    );
    const recordIds = recordsToRemove.map(record => record.id);
    recordGroup.deleteRecords(recordIds);
    recordGroup.notify('entitiesDeleted', recordIds);
  };

  return { tryMergeItemsToCacheStore, tryDeleteItemsInCacheStore };
}
